<template>
    <div
        v-if="isComponentReady"
        :class="wrapperClasses"
        class="l-ss-c-complex-popup"
        v-click-outside="clickOutside">
        <div
            class="l-ss-c-complex-search-results-wrapper"
            ref="resultswrapper"
            @click="closeCompletions"
            v-show="isComplexResultVisible">
            <search-results-wrapper
                :has-query="hasQuery"
                :has-results="hasResults"
                :store="store"
                :search-state="searchState"
                :query="query"
                :total-items-count="totalItemsCount"
                :is-results-visible="isResultsVisible"
                :is-filters-block-visible="isFiltersBlockVisible"
                :request-categories="requestCategories"
                :results-category-id="resultsCategoryId"
                :error-text-completions="errorTextCompletions"
                :error-text-search="errorTextSearch"
                :has-filters="hasFilters"
                :has-loaded-once="hasLoadedOnce"
                :is-completions-loading="isCompletionsLoading"
                :is-search-loading="isSearchLoading"
                :loading-by-filters="loadingByFilters"
                :limit="searchQueryLimit"
                :search-response="searchResponse"
                :search-request-id="searchRequestId"
                :skip="skip"
                :offer-templates="offerTemplates"
                :filter-items="filterItems"
                :sort-by="sortBy"
                :sort-dest="sortDest"
                :currency-in-offers="currencyInOffers"
                :filter-values="filterValues"
                :from_completions="from_completions"
                :search="search"
                :is-show-signature="isShowSignature"
                :adult-permission-confirmed="adultPermissionConfirmed"
                v-on="searchResultsWrapperEventListeners"/>
        </div>
        <div
            class="l-ss-c-completions-wrapper"
            ref="completionswrapper"
            v-show="isCompletionsVisible">
            <div class="l-ss-c-completions-wrapper_child">
                <completions-wrapper
                    ref="completions-wrapper"
                    :completions="completions"
                    :query="query"
                    :taps-complitions="tapsCompletions"
                    :is-show-signature="isShowSignature"
                    v-on="completionsWrapperEventListeners"
                    @click="handleClick"/>
            </div>
        </div>
    </div>
</template>

<script>
    import { installPlugins } from '../core/plugins';
    import CompletionsMixin from '../core/mixins/completions';
    import RootComponentMixin from '../core/mixins/root-component';
    import CompletionsMethods from '../core/mixins/completions-methods';
    import CompletionsWrapper from '../core/components/completions/completions-wrapper';
    import SearchResultsMixin from '../core/mixins/search-results-mixin';
    import SearchResultsWrapper from '../core/components/search-results/search-results-wrapper';
    import ClickOutside from '../core/directives/clickOutside.js';
    import { DEFAULT_QUERY_LIMIT } from '../config';
    import EventEmitterMixin from '../core/mixins/event-emitter';
    import {
        mapHandlers,
        commonOfferEventHandlers,
        commonSearchResultsWrapperEventHandlers,
    } from '../core/mixins/event-emitter';

    const DEFAULT_QUERY_SKIP = 0;

    installPlugins();

    export default {
        components: {
            CompletionsWrapper,
            SearchResultsWrapper,
        },
        directives: {
            'click-outside': ClickOutside,
        },
        mixins: [
            CompletionsMethods,
            CompletionsMixin,
            SearchResultsMixin,
            EventEmitterMixin,
        ],
        provide: RootComponentMixin.provide,
        props: {
            ...RootComponentMixin.props,
            ...CompletionsMixin.props,
            search: {
                type: Object,
                required: false,
                default: () => null,
            },
            completions: {
                type: Array,
                default: () => ([]),
            },
            query: {
                type: String,
                default: '',
            },
            shouldInitCompletions: {
                type: Boolean,
                default: false,
            },
            hasQuery: {
                type: [String, Boolean],
                default: false,
            },
            requestCategories: {
                type: Object,
                required: false,
                default: () => null,
            },
            from_completions: {
                type: String,
                default: '',
            },
            requestSearch: {
                type: Object,
                required: false,
                default: () => null,
            },
            hasFilters: {
                type: [String, Boolean],
                default: false,
            },
            hasResults: {
                type: [String, Boolean],
                default: false,
            },
            hasLoadedOnce: {
                type: [String, Boolean],
                default: false,
            },
            limit: {
                type: [String, Number],
                default: DEFAULT_QUERY_LIMIT,
            },
            skip: {
                type: [String, Number],
                default: DEFAULT_QUERY_SKIP,
            },
            sortBy: {
                type: String,
                default: '',
            },
            sortDest: {
                type: String,
                default: '',
            },
            adultPermissionConfirmed: {
                type: Boolean,
                default: false,
            },
            isCompletionsVisible: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            ...RootComponentMixin.computed,
            ...SearchResultsMixin.computed,

            isComplexResultVisible() {
                return (this.hideResults === false &&
                    !this.showCompletions &&
                    (this.hasResults || this.requestSearch.isReady)) || this.base64Image || this.isSearchLoading;
            },
            searchQueryLimit() {
                return this.$configuration.searchQuery.limit || this.limit;
            },
            base64Image() {
                return this.store.state.base64Image;
            },
            searchResultsWrapperEventListeners() {
                return {
                    ...mapHandlers(this, commonOfferEventHandlers),
                    ...mapHandlers(this, commonSearchResultsWrapperEventHandlers),
                    'toggle-filters': this.toggleFilters,
                    // TODO: Возможно этот обработчик не нужен, так как внутри search-results-wrapper не эмитится событие handle-search
                    'handle-search': () => {
                        this.onSearchInputAction('handle-search', this);
                    },
                };
            },
            completionsWrapperEventListeners() {
                return {
                    'search-all': this.emitSearchAll,
                    'preview-tab-click': this.emitPreviewTabClick,
                    'reset-query': this.emitResetQuery,
                    'remove-history': this.emitRemoveHistory,
                    'search-input-change': this.emitSearchInputChange,
                    'handle-tags-click': args => this.onTapAction(args),
                };
            },
        },
        mounted() {
            this.$nextTick(() => {
                const completionsEl = this.$refs.completionswrapper;
                const resultsEl = this.$refs.resultswrapper;

                this.$emit('mounted', { completionsEl, resultsEl });
            });
        },
        methods: {
            ...RootComponentMixin.methods,

            clickOutside(event) {
                this.emitClickOutsideWidget(event);
            },
            closeCompletions(event) {
                if (this.$refs.resultswrapper.contains(event.target)) {
                    this.$emit('close-completions');
                }
            },
            handleClick(data) {
                this.store.commit('ui/SET_PROPERTY', { key: 'isCategorySearch', value: false });

                const handler = (completion) => {
                    this.$emit('click-item', {
                        ...completion, // TODO: deprecated [удалить c 01.03]
                        completion: completion,
                    });
                };

                this.completionClickHandler(data, handler.bind(this));
            },
            onTapAction(params) {
                this.emitSearchInputChange(params);
                this.emitHandleSearch();
            },
        },
    };
</script>
